import React, { createContext, useContext, useState, ReactNode } from 'react';

interface LanguageContextType {
  language: 'en' | 'sw';
  setLanguage: (lang: 'en' | 'sw') => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.about': 'About Us',
    'nav.services': 'Services',
    'nav.directories': 'Directories',
    'nav.resources': 'Resources',
    'nav.socialWelfare': 'Social Welfare',
    'nav.appointment': 'Appointment',
    'nav.contact': 'Contact',
    'nav.emergency': 'Emergency',
    
    // Home Page
    'home.title': 'Welcome to Rabininsia Memorial Hospital',
    'home.subtitle': 'Delivering Quality Healthcare for All in the heart of Dar es Salaam, Tanzania. We are committed to providing high-quality, affordable, and compassionate healthcare services.',
    'home.bookAppointment': 'Book Appointment',
    'home.ourServices': 'Our Services',
    'home.qualityHealthcare': 'Quality Healthcare',
    'home.emergencyCare': 'Emergency Care',
    'home.specialists': 'Specialists',
    'home.patientsServed': 'Patients Served',
    'home.yearsExcellence': 'Years of Excellence',
    'home.latestNews': 'Latest News & Updates',
    'home.stayInformed': 'Stay informed about our latest developments and services',
    'home.keyServices': 'Our Key Services',
    'home.comprehensiveHealthcare': 'Comprehensive healthcare services for all your medical needs',
    'home.viewAllServices': 'View All Services',
    'home.medicalEmergency': 'Medical Emergency?',
    'home.emergencyDept': 'Our emergency department is open 24/7 to provide immediate medical care',
    'home.emergencyHotline': 'Emergency Hotline',
    'home.alwaysAvailable': 'Always Available',
    'home.readyToSchedule': 'Ready to Schedule Your Visit?',
    'home.bookWithProfessionals': 'Book an appointment with our experienced medical professionals today',
    'home.bookNow': 'Book Appointment Now',
    
    // About Page
    'about.title': 'About Rabininsia Memorial Hospital',
    'about.subtitle': 'Learn about our commitment to providing exceptional healthcare services to the community of Dar es Salaam and beyond.',
    'about.executiveDirector': 'Executive Director',
    'about.directorNote': 'Executive Director\'s Note',
    'about.directorQuote': '"At Rabininsia, we don\'t just treat illness — we care for lives with dignity and integrity."',
    'about.mission': 'Our Mission',
    'about.missionText': 'To provide sustainable, effective, and high-quality medical services that promote health, healing, and hope.',
    'about.vision': 'Our Vision',
    'about.visionText': 'To become a recognized center of medical excellence in Tanzania by 2030.',
    'about.values': 'Our Values',
    'about.valuesText': 'The principles that guide our daily operations and patient care.',
    'about.coreValues': 'Core Values',
    'about.compassion': 'Compassion',
    'about.integrity': 'Integrity',
    'about.innovation': 'Innovation',
    'about.excellence': 'Excellence',
    'about.teamwork': 'Teamwork',
    
    // Services
    'services.title': 'Our Medical Services',
    'services.subtitle': 'Comprehensive healthcare services delivered by experienced medical professionals using state-of-the-art equipment and technology.',
    'services.departments': 'Departments & Services',
    'services.wideRange': 'Our hospital offers a wide range of medical services across multiple specialties',
    'services.emergencyCasualty': 'Emergency & Casualty',
    'services.internalMedicine': 'Internal Medicine',
    'services.pediatrics': 'Pediatrics & Child Health',
    'services.surgery': 'Surgery (General & Specialized)',
    'services.obstetrics': 'Obstetrics & Gynecology',
    'services.dental': 'Dental Services',
    'services.psychiatry': 'Psychiatry & Mental Health',
    'services.physiotherapy': 'Physiotherapy & Rehabilitation',
    'services.radiology': 'Radiology & Diagnostic Imaging',
    'services.laboratory': 'Laboratory Investigations',
    'services.pharmacy': 'Pharmacy Services',
    
    // Appointment
    'appointment.title': 'Book an Appointment',
    'appointment.subtitle': 'Schedule your visit with our experienced medical professionals. We\'re here to provide you with the best healthcare services.',
    'appointment.details': 'Appointment Details',
    'appointment.fullName': 'Full Name',
    'appointment.phoneNumber': 'Phone Number',
    'appointment.emailAddress': 'Email Address',
    'appointment.department': 'Department',
    'appointment.selectDepartment': 'Select Department',
    'appointment.preferredDate': 'Preferred Date',
    'appointment.preferredTime': 'Preferred Time',
    'appointment.selectTime': 'Select Time',
    'appointment.additionalMessage': 'Additional Message or Notes',
    'appointment.bookAppointment': 'Book Appointment',
    'appointment.booking': 'Booking Appointment...',
    'appointment.success': 'Appointment Booked Successfully!',
    'appointment.thankYou': 'Thank you for booking your appointment with Rabininsia Memorial Hospital. You will receive a confirmation via SMS or email shortly.',
    
    // Contact
    'contact.title': 'Contact Us',
    'contact.subtitle': 'Get in touch with Rabininsia Memorial Hospital. We\'re here to help you with your healthcare needs and answer any questions you may have.',
    'contact.getInTouch': 'Get in Touch',
    'contact.address': 'Address',
    'contact.phoneNumbers': 'Phone Numbers',
    'contact.email': 'Email',
    'contact.visitingHours': 'Visiting Hours',
    'contact.weekdays': 'Weekdays',
    'contact.weekends': 'Weekends & Public Holidays',
    'contact.sendMessage': 'Send us a Message',
    'contact.firstName': 'First Name',
    'contact.lastName': 'Last Name',
    'contact.subject': 'Subject',
    'contact.message': 'Message',
    'contact.sendMessageBtn': 'Send Message',
    
    // Footer
    'footer.qualityHealthcare': 'Delivering quality healthcare with compassion and excellence in Dar es Salaam, Tanzania.',
    'footer.quickLinks': 'Quick Links',
    'footer.usefulLinks': 'Useful Links',
    'footer.copyright': '© 2025 Rabininsia Memorial Hospital. All Rights Reserved.',
    'footer.designedBy': 'Website Designed and Managed by Rabininsia IT Department',
    
    // Popup
    'popup.needDoctor': 'Need to see a doctor?',
    'popup.bookNow': 'Book appointment now',
    'popup.bookAppointment': 'Book Appointment',
    'popup.close': 'Close',
  },
  sw: {
    // Navigation
    'nav.home': 'Nyumbani',
    'nav.about': 'Kuhusu Sisi',
    'nav.services': 'Huduma',
    'nav.directories': 'Orodha',
    'nav.resources': 'Rasilimali',
    'nav.socialWelfare': 'Ustawi wa Kijamii',
    'nav.appointment': 'Miadi',
    'nav.contact': 'Mawasiliano',
    'nav.emergency': 'Dharura',
    
    // Home Page
    'home.title': 'Karibu Hospitali ya Kumbukumbu ya Rabininsia',
    'home.subtitle': 'Kutoa Huduma za Afya za Ubora kwa Wote katika moyo wa Dar es Salaam, Tanzania. Tumejitolea kutoa huduma za afya za ubora wa juu, za bei nafuu, na za huruma.',
    'home.bookAppointment': 'Weka Miadi',
    'home.ourServices': 'Huduma Zetu',
    'home.qualityHealthcare': 'Huduma za Afya za Ubora',
    'home.emergencyCare': 'Huduma za Dharura',
    'home.specialists': 'Wataalamu',
    'home.patientsServed': 'Wagonjwa Waliotumikiwa',
    'home.yearsExcellence': 'Miaka ya Ubora',
    'home.latestNews': 'Habari na Masasisho ya Hivi Karibuni',
    'home.stayInformed': 'Pata taarifa kuhusu maendeleo na huduma zetu za hivi karibuni',
    'home.keyServices': 'Huduma Zetu Kuu',
    'home.comprehensiveHealthcare': 'Huduma kamili za afya kwa mahitaji yako yote ya kimatibabu',
    'home.viewAllServices': 'Ona Huduma Zote',
    'home.medicalEmergency': 'Dharura ya Kimatibabu?',
    'home.emergencyDept': 'Idara yetu ya dharura inafanya kazi saa 24/7 kutoa matibabu ya haraka',
    'home.emergencyHotline': 'Simu ya Dharura',
    'home.alwaysAvailable': 'Inapatikana Kila Wakati',
    'home.readyToSchedule': 'Uko Tayari Kupanga Ziara Yako?',
    'home.bookWithProfessionals': 'Weka miadi na wataalamu wetu wenye uzoefu wa kimatibabu leo',
    'home.bookNow': 'Weka Miadi Sasa',
    
    // About Page
    'about.title': 'Kuhusu Hospitali ya Kumbukumbu ya Rabininsia',
    'about.subtitle': 'Jifunze kuhusu dhamira yetu ya kutoa huduma bora za afya kwa jamii ya Dar es Salaam na zaidi.',
    'about.executiveDirector': 'Mkurugenzi Mtendaji',
    'about.directorNote': 'Ujumbe wa Mkurugenzi Mtendaji',
    'about.directorQuote': '"Huko Rabininsia, hatutibu ugonjwa tu — tunajali maisha kwa heshima na uongozi."',
    'about.mission': 'Dhamira Yetu',
    'about.missionText': 'Kutoa huduma za kimatibabu endelevu, za ufanisi, na za ubora wa juu zinazokoza afya, uponyaji, na matumaini.',
    'about.vision': 'Maono Yetu',
    'about.visionText': 'Kuwa kituo kinachotambuliwa cha ubora wa kimatibabu nchini Tanzania erbani mwaka 2030.',
    'about.values': 'Maadili Yetu',
    'about.valuesText': 'Kanuni zinazoongoza shughuli zetu za kila siku na huduma kwa wagonjwa.',
    'about.coreValues': 'Maadili Makuu',
    'about.compassion': 'Huruma',
    'about.integrity': 'Uongozi',
    'about.innovation': 'Uvumbuzi',
    'about.excellence': 'Ubora',
    'about.teamwork': 'Kazi ya Pamoja',
    
    // Services
    'services.title': 'Huduma Zetu za Kimatibabu',
    'services.subtitle': 'Huduma kamili za afya zinazotolewa na wataalamu wa kimatibabu wenye uzoefu wakitumia vifaa vya kisasa na teknolojia.',
    'services.departments': 'Idara na Huduma',
    'services.wideRange': 'Hospitali yetu inatoa huduma mbalimbali za kimatibabu katika utaalamu mwingi',
    'services.emergencyCasualty': 'Dharura na Ajali',
    'services.internalMedicine': 'Dawa za Ndani',
    'services.pediatrics': 'Afya ya Watoto',
    'services.surgery': 'Upasuaji (wa Jumla na wa Utaalamu)',
    'services.obstetrics': 'Uzazi na Afya ya Wanawake',
    'services.dental': 'Huduma za Meno',
    'services.psychiatry': 'Afya ya Akili',
    'services.physiotherapy': 'Tiba ya Mwili',
    'services.radiology': 'Upimaji wa Picha za Ndani',
    'services.laboratory': 'Uchunguzi wa Maabara',
    'services.pharmacy': 'Huduma za Duka la Dawa',
    
    // Appointment
    'appointment.title': 'Weka Miadi',
    'appointment.subtitle': 'Panga ziara yako na wataalamu wetu wa kimatibabu wenye uzoefu. Tuko hapa kukupa huduma bora za afya.',
    'appointment.details': 'Maelezo ya Miadi',
    'appointment.fullName': 'Jina Kamili',
    'appointment.phoneNumber': 'Nambari ya Simu',
    'appointment.emailAddress': 'Anwani ya Barua Pepe',
    'appointment.department': 'Idara',
    'appointment.selectDepartment': 'Chagua Idara',
    'appointment.preferredDate': 'Tarehe Unayopendelea',
    'appointment.preferredTime': 'Wakati Unaopendelea',
    'appointment.selectTime': 'Chagua Wakati',
    'appointment.additionalMessage': 'Ujumbe au Maelezo ya Ziada',
    'appointment.bookAppointment': 'Weka Miadi',
    'appointment.booking': 'Inaweka Miadi...',
    'appointment.success': 'Miadi Imewekwa Kwa Mafanikio!',
    'appointment.thankYou': 'Asante kwa kuweka miadi yako na Hospitali ya Kumbukumbu ya Rabininsia. Utapokea uthibitisho kupitia SMS au barua pepe hivi karibuni.',
    
    // Contact
    'contact.title': 'Wasiliana Nasi',
    'contact.subtitle': 'Wasiliana na Hospitali ya Kumbukumbu ya Rabininsia. Tuko hapa kukusaidia na mahitaji yako ya afya na kujibu maswali yoyote unayoweza kuwa nayo.',
    'contact.getInTouch': 'Wasiliana',
    'contact.address': 'Anwani',
    'contact.phoneNumbers': 'Nambari za Simu',
    'contact.email': 'Barua Pepe',
    'contact.visitingHours': 'Masaa ya Kutembelea',
    'contact.weekdays': 'Siku za Wiki',
    'contact.weekends': 'Wikendi na Sikukuu za Umma',
    'contact.sendMessage': 'Tutumie Ujumbe',
    'contact.firstName': 'Jina la Kwanza',
    'contact.lastName': 'Jina la Mwisho',
    'contact.subject': 'Mada',
    'contact.message': 'Ujumbe',
    'contact.sendMessageBtn': 'Tuma Ujumbe',
    
    // Footer
    'footer.qualityHealthcare': 'Kutoa huduma za afya za ubora kwa huruma na ubora huko Dar es Salaam, Tanzania.',
    'footer.quickLinks': 'Viungo vya Haraka',
    'footer.usefulLinks': 'Viungo Muhimu',
    'footer.copyright': '© 2025 Hospitali ya Kumbukumbu ya Rabininsia. Haki Zote Zimehifadhiwa.',
    'footer.designedBy': 'Tovuti Imeundwa na Kusimamizwa na Idara ya IT ya Rabininsia',
    
    // Popup
    'popup.needDoctor': 'Unahitaji kuona daktari?',
    'popup.bookNow': 'Weka miadi sasa',
    'popup.bookAppointment': 'Weka Miadi',
    'popup.close': 'Funga',
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<'en' | 'sw'>('en');

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};